import type { CSSProperties, FC } from "react";

import { initials } from "@/components/mentions/mentions";
import { cn } from "@/lib/utils";

import type { CommentPinInfo } from "../commentsModel";

type Props = {
  pin: CommentPinInfo;
  /** Open the pin's thread in the Comments pane. */
  onOpen: (threadId: string) => void;
  /** Positioning on the page overlay (the host places it on the field's right edge). */
  className?: string;
  style?: CSSProperties;
};

/**
 * The marker on a field with comments: the thread author's initials, and the
 * count when there is more than one comment. Accent while a thread is open.
 */
export const CommentPin: FC<Props> = ({ pin, onOpen, className, style }) => {
  const label = `${pin.count} ${pin.count === 1 ? "Comment" : "Comments"}`;
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      data-slot="form-comment-pin"
      data-resolved={pin.resolved ? "true" : undefined}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(pin.threadId);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded-full bg-elevated p-0.5 shadow-popover ring-1 transition-colors duration-100 hover:bg-fill-1",
        pin.resolved ? "ring-line-3" : "ring-accent-seed",
        pin.count > 1 && "pr-1.5",
        className
      )}
    >
      <span
        aria-hidden
        className={cn(
          "inline-flex size-4 items-center justify-center rounded-full text-xs font-medium",
          pin.resolved
            ? "bg-fill-2 text-fg-3"
            : "bg-accent-soft text-accent-seed"
        )}
      >
        {initials(pin.authorName || pin.authorUid)}
      </span>
      {pin.count > 1 ? (
        <span className="text-xs font-medium text-fg-2 tabular-nums">
          {pin.count}
        </span>
      ) : null}
    </button>
  );
};
